import { Link } from '@tanstack/react-router'
import { StatTile } from '../components/StatTile.tsx'
import { useSelectedCampaign } from '../lib/campaign.tsx'
import { roleLabel } from '../lib/roles.ts'
import { useCampaignTotals } from '../lib/totals.ts'

/**
 * A campaign's front page (2026-09-30 layout): how the field is going, what the
 * campaign bought, and where its access stands. Aggregates only.
 *
 * The old Overview — the canonical layer's row counts — was platform business
 * and now lives under Platform › Voter data.
 */
export function CampaignOverview() {
  const { campaign, campaignId } = useSelectedCampaign()
  const t = useCampaignTotals(campaignId, Boolean(campaign?.can_run_counts && campaign.may_report))

  if (!campaign) {
    return <p className="text-sm text-ink-muted">Loading…</p>
  }

  const phones = campaign.dialer_seats > 0

  return (
    <div>
      <h1 className="text-xl font-semibold tracking-tight">{campaign.name}</h1>
      <p className="mt-1 text-sm text-ink-muted">
        {campaign.org_name} · you are {roleLabel(campaign.role)}
        {campaign.election_date ? ` · election ${campaign.election_date}` : ''}
      </p>

      <AccessNote />

      {/* The two big jobs (owner, 2026-10-25, row W1). */}
      {campaign.may_cut && (campaign.can_manage_canvassers || campaign.can_build_universes || campaign.can_cut_walk_lists) ? (
        <div className="mt-6 flex flex-wrap gap-3">
          {campaign.can_manage_canvassers && (campaign.walk_purchased || campaign.walk_app) ? (
            <Link to="/canvasses/new" className="rounded-lg bg-brand-900 px-5 py-3 text-base font-semibold text-white hover:bg-brand-800">
              New canvass
            </Link>
          ) : null}
          {campaign.can_build_universes || campaign.can_cut_walk_lists ? (
            <Link to="/lists" className="rounded-lg border border-brand-400 px-5 py-3 text-base font-semibold text-brand-900 hover:bg-surface-sunken">
              Cut a list
            </Link>
          ) : null}
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile
          label="Doors knocked"
          value={campaign.walk_purchased ? t.doors : null}
          hint={campaign.walk_purchased ? 'knocks, not people' : 'walk not purchased'}
        />
        <StatTile
          label="Calls made"
          value={phones ? t.calls : null}
          hint={phones ? 'attempts, not people' : 'phones not purchased'}
        />
        <StatTile label="People reached" value={t.reached} />
        <StatTile label="Supporters" value={t.supporters} hint="latest answer per person" />
      </div>

      <p className="mt-3 text-xs text-ink-muted">
        Last contact recorded: {t.lastContact ? t.lastContact.slice(0, 10) : 'none yet'} ·{' '}
        <Link to="/progress" className="text-brand-700 hover:underline">
          Full progress report
        </Link>
      </p>

      <section className="mt-8">
        <h2 className="text-sm font-semibold tracking-wide text-ink-muted uppercase">What this campaign has</h2>
        <ul className="mt-2 space-y-1 text-sm">
          <li>Lists — universes and list cutting</li>
          <li className={campaign.walk_purchased ? '' : 'text-ink-muted line-through'}>
            Walk — canvasses, paper walk sheets and the walk app
          </li>
          <li className={phones ? '' : 'text-ink-muted line-through'}>
            Phones{phones ? ` — ${campaign.dialer_seats} seats (dialer not built yet)` : ''}
          </li>
        </ul>
      </section>
    </div>
  )

  function AccessNote() {
    if (!campaign) return null
    if (campaign.may_cut) {
      return campaign.cut_until ? (
        <p className="mt-4 text-sm text-ink-muted">
          Lists can be cut until {campaign.cut_until}; reports stay open until{' '}
          {campaign.report_until ?? '—'}.
        </p>
      ) : null
    }
    return (
      <p role="status" className="mt-4 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm">
        {campaign.may_report
          ? `Cutting has closed. Reports and contact history stay open until ${campaign.report_until ?? '—'}.`
          : 'This campaign has no active purchase. Its data is kept for when it renews.'}
      </p>
    )
  }
}
